/**
 * @file useModerationNotifications.js
 * @brief Handles browser notifications for new moderation queue entries.
 */
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Create notification helpers for moderation queue activity.
 * @param {Object} params Hook parameters.
 * @param {string} params.pathname Current route path used to suppress notifications on the moderation screen.
 * @returns {{notifyIncomingModerationListing: Function}} Notification helper for moderation events.
 */
export function useModerationNotifications({ pathname }) {
    const { t } = useTranslation();
    const warnedRef = useRef(false);
    const windowNotificationsRef = useRef(new Map());

    const moderationNotificationTag = useCallback((listingId) => {
        return `moderation-${listingId}`;
    }, []);

    const canUseNotifications = useCallback(() => {
        if (typeof window === "undefined") return false;
        if (!("Notification" in window)) {
            if (!warnedRef.current) {
                console.warn(t("notifications.unsupported"));
                warnedRef.current = true;
            }
            return false;
        }
        if (!window.isSecureContext) {
            if (!warnedRef.current) {
                console.warn(t("notifications.httpsRequired"));
                warnedRef.current = true;
            }
            return false;
        }
        return true;
    }, [t]);

    const notifyIncomingModerationListing = useCallback((moderationEvent = {}) => {
        if (!canUseNotifications()) return;
        if (Notification.permission !== "granted") return;

        const isModerationPage = pathname?.startsWith("/moderation");
        if (isModerationPage && document.visibilityState === "visible") return;

        const listingId = moderationEvent?.listing_id;
        if (!listingId) return;

        const isQueueUpdate = moderationEvent?.event === "queue_update";
        const listingTitle = moderationEvent?.listing_title || t("notifications.moderationListingFallbackTitle");
        const authorName = moderationEvent?.author_username || t("notifications.someone");
        const addedCount = Number(moderationEvent?.count || 0);

        const title = isQueueUpdate
            ? t("notifications.newModerationListingsTitle")
            : t("notifications.newModerationListingTitle");
        const body = isQueueUpdate
            ? t("notifications.newModerationListingsBody", { count: addedCount })
            : t("notifications.newModerationListingBody", {
                title: listingTitle,
                author: authorName,
            });

        const targetUrl = "/moderation";
        const tag = moderationNotificationTag(listingId);
        const options = {
            body,
            tag,
            renotify: true,
            data: { url: targetUrl },
        };

        /**
         * Show the moderation notification with the window API when service workers are unavailable.
         * @returns {void}
         */
        const fallbackShow = () => {
            const notification = new Notification(title, options);
            windowNotificationsRef.current.set(tag, notification);
            notification.onclick = () => {
                window.focus();
                window.location.assign(targetUrl);
                notification.close();
                windowNotificationsRef.current.delete(tag);
            };
            notification.onclose = () => {
                windowNotificationsRef.current.delete(tag);
            };
        };

        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistration()
                .then((registration) => {
                    if (registration) {
                        return registration.showNotification(title, options);
                    }
                    fallbackShow();
                    return null;
                })
                .catch(() => {
                    fallbackShow();
                });
            return;
        }

        fallbackShow();
    }, [canUseNotifications, moderationNotificationTag, pathname, t]);

    return { notifyIncomingModerationListing };
}
